(() => {
  const clamp = (value, min = 0, max = 1) => Math.max(min, Math.min(max, value));
  const mix = (a, b, progress) => a + (b - a) * progress;
  const wrap = (value, length) => ((value % length) + length) % length;
  const smooth = (value) => { const t = clamp(value); return t * t * (3 - 2 * t); };
  const quint = (value) => {
    const t = clamp(value);
    return t ** 3 * (t * (t * 6 - 15) + 10);
  };
  const landing = (value, overshoot) => {
    const t = clamp(value);
    return quint(t) + Math.sin(Math.PI * t) * overshoot * t;
  };
  const number = (value, fallback = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };
  class MobileHomeStage extends HTMLElement {
    connectedCallback() {
      this.stage = this.querySelector('.mobile-home-stage');
      this.track = this.querySelector('.mobile-home-stage__track');
      if (!this.stage || !this.track) return;

      this.scenes = Array.from(this.querySelectorAll('.mobile-home-scene'), (element, index) => ({
        element,
        link: element.querySelector('.mobile-home-scene__link'),
        artwork: element.querySelector('.mobile-home-scene__artwork'),
        details: element.querySelector('.mobile-home-scene__details'),
        scale: clamp(Number(element.dataset.scale) || 1, 0.6, 1.2),
        x: clamp(Number(element.dataset.offsetX) || 0, -40, 40),
        y: clamp(Number(element.dataset.offsetY) || 0, -60, 60)
      }));
      if (!this.scenes.length) return;

      this.viewButtons = Array.from(this.querySelectorAll('[data-view-mode]'));
      this.viewStorageKey = 'ennui-mobile-product-view-v1';
      this.desktop = window.matchMedia('(min-width: 750px)');
      try {
        this.viewMode = window.localStorage.getItem(this.viewStorageKey) === 'grid' ? 'grid' : 'belt';
      } catch (error) {
        this.viewMode = 'belt';
      }
      this.onViewClick = (event) => this.setViewMode(event.currentTarget.dataset.viewMode, true);
      this.viewButtons.forEach(button => button.addEventListener('click', this.onViewClick));

      this.stickers = Array.from(this.querySelectorAll('.sticker-layer__item'), (element, index) => ({
        element,
        index,
        opacity: clamp(number(element.dataset.stickerOpacity, 1)),
        start: clamp(number(element.dataset.stickerStart)),
        duration: clamp(number(element.dataset.stickerDuration, 0.18), 0.05, 1),
        x: number(element.dataset.stickerEntryX),
        y: number(element.dataset.stickerEntryY),
        rotation: number(element.dataset.stickerEntryRotation),
        scale: clamp(number(element.dataset.stickerEntryScale, 1), 0.4, 1.6),
        overshoot: clamp(number(element.dataset.stickerOvershoot, 0.08), 0, 0.25),
        originX: clamp(number(element.dataset.stickerOriginX, 50), 0, 100),
        originY: clamp(number(element.dataset.stickerOriginY, 50), 0, 100),
        shadow: clamp(number(element.dataset.stickerShadow, 0.18), 0, 0.4),
        driftX: clamp(number(element.dataset.stickerDriftX), -3, 3),
        driftY: clamp(number(element.dataset.stickerDriftY), -3, 3),
        driftRotation: clamp(number(element.dataset.stickerDriftRotation), -2, 2),
        lastProgress: null
      }));

      this.mobile = window.matchMedia('(min-width: 0px)');
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.onModeChange = () => {
        this.setViewMode(this.desktop.matches ? 'grid' : this.viewMode, false);
      };
      this.onResize = () => {
        this.stageHeight = Math.max(1, this.stage.clientHeight || window.innerHeight);
        this.render(this.currentPosition ?? 0, true);
      };
      this.onVisibility = () => {
        this.toggleAttribute('data-motion-paused', document.hidden);
        if (document.hidden) {
          cancelAnimationFrame(this.frame);
          this.frame = null;
        } else {
          this.schedule();
        }
      };
      this.onWheel = this.handleWheel.bind(this);
      this.onTouchStart = this.handleTouchStart.bind(this);
      this.onTouchMove = this.handleTouchMove.bind(this);
      this.onTouchEnd = this.handleTouchEnd.bind(this);
      this.onClickCapture = this.handleClickCapture.bind(this);
      this.onKeyDown = this.handleKey.bind(this);
      this.onBlockSelect = (event) => {
        const index = this.scenes.findIndex(scene => scene.element.dataset.blockId === event.detail?.blockId);
        if (index >= 0 && this.running) this.goToScene(index);
      };

      this.mobile.addEventListener('change', this.onModeChange);
      this.desktop.addEventListener('change', this.onModeChange);
      this.reduced.addEventListener('change', this.onModeChange);
      this.addEventListener('shopify:block:select', this.onBlockSelect);
      this.onModeChange();
    }

    disconnectedCallback() {
      this.stop();
      this.viewButtons?.forEach(button => button.removeEventListener('click', this.onViewClick));
      document.body.classList.remove('mobile-home-grid-active');
      this.mobile?.removeEventListener('change', this.onModeChange);
      this.desktop?.removeEventListener('change', this.onModeChange);
      this.reduced?.removeEventListener('change', this.onModeChange);
      this.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    setViewMode(mode, persist = false) {
      const desktopForced = this.desktop?.matches;
      const nextMode = desktopForced ? 'grid' : (mode === 'grid' ? 'grid' : 'belt');
      if (!desktopForced) this.viewMode = nextMode;
      if (persist && !desktopForced) {
        try { window.localStorage.setItem(this.viewStorageKey, nextMode); } catch (error) {}
      }

      this.viewButtons.forEach((button) => {
        const active = button.dataset.viewMode === nextMode;
        button.setAttribute('aria-pressed', String(active));
      });

      if (!this.mobile.matches) return;
      if (nextMode === 'grid') {
        this.stop();
        this.classList.add('is-enhanced', 'is-grid-view');
        document.body.classList.add('mobile-home-grid-active');
        this.scenes.forEach(({ element, link, artwork }) => {
          element.style.removeProperty('visibility');
          element.style.removeProperty('transform');
          element.classList.add('is-visible', 'is-active');
          element.setAttribute('aria-hidden', 'false');
          element.inert = false;
          link.tabIndex = 0;
          artwork.style.removeProperty('transform');
          artwork.style.removeProperty('filter');
        });
        return;
      }

      document.body.classList.remove('mobile-home-grid-active');
      this.classList.remove('is-grid-view');
      if (!this.running) this.start();
      else this.render(this.currentPosition, true);
    }

    start() {
      if (this.running) return;
      this.running = true;
      this.classList.add('is-enhanced');
      this.track.style.setProperty('--scene-count', this.scenes.length);
      this.currentPosition = Number.isFinite(this.currentPosition) ? this.currentPosition : 0;
      this.targetPosition = this.currentPosition;
      this.activeIndex = wrap(Math.round(this.currentPosition), this.scenes.length);
      this.lastPosition = null;
      this.lastTick = null;
      this.stageHeight = Math.max(1, this.stage.clientHeight || window.innerHeight);
      this.stickerTravel = Number.isFinite(this.stickerTravel) ? this.stickerTravel : 0;
      this.stickerBuildDistance = Math.max(8, Math.min(12, this.scenes.length * 1.05));
      this.lastStickerPosition = this.currentPosition;
      this.lastStickerProgress = null;
      this.lastStickerScenePosition = null;

      window.addEventListener('wheel', this.onWheel, { passive: false });
      window.addEventListener('resize', this.onResize, { passive: true });
      window.addEventListener('orientationchange', this.onResize);
      document.addEventListener('visibilitychange', this.onVisibility);
      this.stage.addEventListener('touchstart', this.onTouchStart, { passive: true });
      this.stage.addEventListener('touchmove', this.onTouchMove, { passive: false });
      this.stage.addEventListener('touchend', this.onTouchEnd, { passive: true });
      this.stage.addEventListener('touchcancel', this.onTouchEnd, { passive: true });
      this.addEventListener('click', this.onClickCapture, true);
      this.addEventListener('keydown', this.onKeyDown);
      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(this.stage);

      // The product belt owns the mobile viewport. Clear Safari's restored
      // document position once, then keep all movement inside the stage.
      if (window.scrollY) window.scrollTo({ top: 0, behavior: 'instant' });
      this.render(this.currentPosition, true);
    }

    stop() {
      if (!this.running) return;
      this.running = false;
      this.cancelMotion();
      this.resizeObserver?.disconnect();
      window.removeEventListener('wheel', this.onWheel);
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('orientationchange', this.onResize);
      document.removeEventListener('visibilitychange', this.onVisibility);
      this.stage.removeEventListener('touchstart', this.onTouchStart);
      this.stage.removeEventListener('touchmove', this.onTouchMove);
      this.stage.removeEventListener('touchend', this.onTouchEnd);
      this.stage.removeEventListener('touchcancel', this.onTouchEnd);
      this.removeEventListener('click', this.onClickCapture, true);
      this.removeEventListener('keydown', this.onKeyDown);
      this.classList.remove('is-enhanced');
      this.removeAttribute('data-motion-paused');
      document.body.style.removeProperty('--mobile-home-ink');
      this.scenes.forEach(({ element, link, artwork, details }) => {
        element.style.removeProperty('visibility');
        element.style.removeProperty('transform');
        element.classList.remove('is-active', 'is-visible');
        element.removeAttribute('aria-hidden');
        element.inert = false;
        link.removeAttribute('tabindex');
        artwork.style.removeProperty('transform');
        artwork.style.removeProperty('filter');
        details.style.removeProperty('transform');
      });
      this.stickers.forEach(({ element }) => {
        element.classList.remove('is-sticker-moving');
        element.style.removeProperty('--sticker-motion-x');
        element.style.removeProperty('--sticker-motion-y');
        element.style.removeProperty('--sticker-motion-rotation');
        element.style.removeProperty('--sticker-motion-scale');
        element.style.removeProperty('--sticker-motion-origin-x');
        element.style.removeProperty('--sticker-motion-origin-y');
        element.style.removeProperty('--sticker-render-opacity');
        element.style.removeProperty('--sticker-motion-shadow');
      });
    }

    normalizeWheel(event) {
      if (event.deltaMode === 1) return event.deltaY * 16;
      if (event.deltaMode === 2) return event.deltaY * this.stageHeight;
      return event.deltaY;
    }

    handleWheel(event) {
      if (!this.running || event.ctrlKey || !event.deltaY) return;
      event.preventDefault();

      // Residual trackpad events cannot advance another product while the
      // current move is settling. A fresh gesture begins after a quiet gap.
      if (this.snap) {
        clearTimeout(this.wheelReleaseTimer);
        this.wheelReleaseTimer = setTimeout(() => { this.wheelReady = true; }, 140);
        return;
      }
      if (this.wheelReady === false) {
        clearTimeout(this.wheelReleaseTimer);
        this.wheelReleaseTimer = setTimeout(() => { this.wheelReady = true; }, 140);
        return;
      }
      if (!this.wheelGesture) {
        this.wheelGesture = true;
        this.wheelAnchor = Math.round(this.currentPosition);
        this.wheelPixels = 0;
      }

      this.wheelPixels += this.normalizeWheel(event);
      const travel = clamp(this.wheelPixels / (this.stageHeight * 0.72), -0.92, 0.92);
      this.targetPosition = this.wheelAnchor + travel;
      this.schedule();
      clearTimeout(this.wheelSettleTimer);
      this.wheelSettleTimer = setTimeout(() => this.finishWheel(), 115);
    }

    finishWheel() {
      if (!this.wheelGesture || this.snap) return;
      const anchor = this.wheelAnchor;
      const progress = this.targetPosition - anchor;
      const direction = Math.abs(this.wheelPixels) >= 64 || Math.abs(progress) >= 0.34
        ? Math.sign(this.wheelPixels || progress)
        : 0;
      this.wheelGesture = false;
      this.wheelReady = false;
      this.beginSnap(anchor + direction);
      clearTimeout(this.wheelReleaseTimer);
      this.wheelReleaseTimer = setTimeout(() => { this.wheelReady = true; }, 180);
    }

    handleTouchStart(event) {
      if (!this.running || event.touches.length !== 1) return;
      this.cancelSnap();
      const touch = event.touches[0];
      this.touch = {
        anchor: Math.round(this.currentPosition),
        startY: touch.clientY,
        lastY: touch.clientY,
        lastTime: event.timeStamp || performance.now(),
        velocity: 0,
        moved: false
      };
      this.targetPosition = this.currentPosition;
    }

    handleTouchMove(event) {
      if (!this.touch || event.touches.length !== 1) return;
      event.preventDefault();
      const touch = event.touches[0];
      const time = event.timeStamp || performance.now();
      const elapsed = Math.max(1, time - this.touch.lastTime);
      const delta = this.touch.startY - touch.clientY;
      this.touch.velocity = (this.touch.lastY - touch.clientY) / elapsed;
      this.touch.lastY = touch.clientY;
      this.touch.lastTime = time;
      this.touch.moved ||= Math.abs(delta) > 8;
      const travel = clamp(delta / (this.stageHeight * 0.72), -0.92, 0.92);
      this.currentPosition = this.touch.anchor + travel;
      this.targetPosition = this.currentPosition;
      this.render(this.currentPosition);
    }

    handleTouchEnd() {
      if (!this.touch) return;
      const progress = this.currentPosition - this.touch.anchor;
      const committed = Math.abs(progress) >= 0.16 || (Math.abs(this.touch.velocity) >= 0.45 && Math.abs(progress) >= 0.04);
      const direction = committed ? Math.sign(progress || this.touch.velocity) : 0;
      this.suppressClick = this.touch.moved;
      clearTimeout(this.clickReleaseTimer);
      if (this.suppressClick) {
        this.clickReleaseTimer = setTimeout(() => { this.suppressClick = false; }, 450);
      }
      const destination = this.touch.anchor + direction;
      this.touch = null;
      this.beginSnap(destination);
    }

    handleClickCapture(event) {
      if (!this.suppressClick) return;
      clearTimeout(this.clickReleaseTimer);
      this.suppressClick = false;
      event.preventDefault();
      event.stopImmediatePropagation();
    }

    beginSnap(destination) {
      clearTimeout(this.wheelSettleTimer);
      const from = this.currentPosition;
      this.targetPosition = destination;
      if (this.reduced.matches || Math.abs(from - destination) < 0.0001) {
        this.currentPosition = destination;
        this.snap = null;
        this.render(destination, true);
        return;
      }
      this.snap = { from, to: destination, started: null, duration: 500 };
      this.lastTick = null;
      this.schedule();
    }

    cancelSnap() {
      this.snap = null;
      this.lastTick = null;
      cancelAnimationFrame(this.frame);
      this.frame = null;
    }

    cancelMotion() {
      this.cancelSnap();
      clearTimeout(this.wheelSettleTimer);
      clearTimeout(this.wheelReleaseTimer);
      clearTimeout(this.clickReleaseTimer);
      this.wheelGesture = false;
      this.touch = null;
    }

    schedule() {
      if (!this.running || document.hidden || this.frame) return;
      this.frame = requestAnimationFrame((time) => {
        this.frame = null;
        if (!this.running) return;

        if (this.snap) {
          const snap = this.snap;
          if (snap.started == null) snap.started = time;
          const progress = clamp((time - snap.started) / snap.duration);
          this.currentPosition = mix(snap.from, snap.to, quint(progress));
          this.render(this.currentPosition);
          if (progress < 1) this.schedule();
          else {
            this.currentPosition = snap.to;
            this.targetPosition = snap.to;
            this.snap = null;
            this.lastTick = null;
            this.render(this.currentPosition, true);
          }
          return;
        }

        const elapsed = this.lastTick == null ? 16.67 : Math.min(50, time - this.lastTick);
        this.lastTick = time;
        this.currentPosition = this.reduced.matches
          ? this.targetPosition
          : mix(this.currentPosition, this.targetPosition, 1 - Math.exp(-elapsed / 72));
        const moving = Math.abs(this.targetPosition - this.currentPosition) > 0.0001;
        if (!moving) this.currentPosition = this.targetPosition;
        this.render(this.currentPosition);
        if (moving) this.schedule();
        else this.lastTick = null;
      });
    }

    render(position, force = false) {
      const stickerDelta = this.lastStickerPosition == null ? 0 : Math.abs(position - this.lastStickerPosition);
      this.stickerTravel = Math.min(this.stickerBuildDistance, this.stickerTravel + stickerDelta);
      this.lastStickerPosition = position;
      const stickerProgress = this.reduced.matches ? 1 : clamp(this.stickerTravel / this.stickerBuildDistance);
      this.dataset.stickerProgress = stickerProgress.toFixed(4);
      this.renderStickers(stickerProgress, position, force);

      if (!force && position === this.lastPosition && this.lastReduced === this.reduced.matches) return;
      this.lastPosition = position;
      this.lastReduced = this.reduced.matches;
      const count = this.scenes.length;
      const localPosition = wrap(position, count);
      const active = wrap(Math.round(position), count);
      const previousFocus = this.scenes.find(scene => scene.element.contains(document.activeElement));

      this.scenes.forEach((scene, index) => {
        const isActive = index === active;
        const distance = this.reduced.matches ? 0 : wrap(index - localPosition + count / 2, count) - count / 2;
        const visible = this.reduced.matches ? isActive : Math.abs(distance) < 1.35;
        scene.element.style.visibility = visible ? 'visible' : 'hidden';
        scene.element.classList.toggle('is-visible', visible);
        scene.element.classList.toggle('is-active', isActive);
        scene.element.setAttribute('aria-hidden', String(!visible));
        scene.element.inert = !visible;
        scene.link.tabIndex = visible ? 0 : -1;
        if (!visible) return;

        scene.element.style.transform = `translate3d(0, ${(distance * 100).toFixed(3)}%, 0)`;
        const direction = index % 2 ? -1 : 1;
        const depth = this.reduced.matches ? 0 : smooth(Math.min(1, Math.abs(distance)));
        scene.artwork.style.transform = `translate3d(${(scene.x + distance * 6 * direction).toFixed(2)}px, ${scene.y.toFixed(2)}px, 0) scale(${(scene.scale * (1 - depth * 0.5)).toFixed(4)}) rotate(${(distance * direction * 0.7).toFixed(3)}deg)`;
        // Large animated blur surfaces are particularly expensive in iOS Safari.
        // Scale and position still communicate depth without re-rasterising images.
        scene.artwork.style.filter = 'none';
        const image = scene.element.querySelector('img');
        if (image) image.loading = 'eager';
      });

      if (previousFocus?.element.inert) this.scenes[active].link.focus({ preventScroll: true });
      this.activeIndex = active;
      this.dataset.activeScene = String(active + 1);
    }

    renderStickers(progress, scenePosition, force = false) {
      const sceneChanged = scenePosition !== this.lastStickerScenePosition;
      if (!this.stickers.length || (!force && progress === this.lastStickerProgress && !sceneChanged)) return;
      this.lastStickerProgress = progress;
      this.lastStickerScenePosition = scenePosition;
      const driftPattern = [0, 0.55, -0.35, 0.8, -0.6, 0.2, -0.75, 0.45, -0.2, 0.65];
      const sceneFrom = Math.floor(scenePosition);
      const sceneBlend = smooth(scenePosition - sceneFrom);

      this.stickers.forEach((sticker) => {
        const local = this.reduced.matches ? 1 : clamp((progress - sticker.start) / sticker.duration);
        const hasDrift = sticker.driftX || sticker.driftY || sticker.driftRotation;
        if (!force && local === sticker.lastProgress && (!sceneChanged || !hasDrift)) return;
        sticker.lastProgress = local;

        const landed = landing(local, sticker.overshoot);
        const remainder = 1 - landed;
        const patternOffset = sticker.index * 3;
        const baseDrift = driftPattern[wrap(patternOffset, driftPattern.length)];
        const fromDrift = driftPattern[wrap(sceneFrom + patternOffset, driftPattern.length)] - baseDrift;
        const toDrift = driftPattern[wrap(sceneFrom + 1 + patternOffset, driftPattern.length)] - baseDrift;
        const drift = this.reduced.matches ? 0 : mix(fromDrift, toDrift, sceneBlend) * quint(local);
        const opacity = sticker.opacity * smooth(local);
        const originProgress = quint(local);
        const originX = mix(sticker.originX, 50, originProgress);
        const originY = mix(sticker.originY, 50, originProgress);
        const moving = local > 0 && local < 1 && !this.reduced.matches;
        const shadowOpacity = moving ? Math.sin(Math.PI * local) * sticker.shadow : 0;
        const style = sticker.element.style;

        style.setProperty('--sticker-motion-x', `${(sticker.x * remainder + sticker.driftX * drift).toFixed(3)}vw`);
        style.setProperty('--sticker-motion-y', `${(sticker.y * remainder + sticker.driftY * drift).toFixed(3)}vh`);
        style.setProperty('--sticker-motion-rotation', `${(sticker.rotation * remainder + sticker.driftRotation * drift).toFixed(3)}deg`);
        style.setProperty('--sticker-motion-scale', mix(sticker.scale, 1, landed).toFixed(4));
        style.setProperty('--sticker-motion-origin-x', `${originX.toFixed(2)}%`);
        style.setProperty('--sticker-motion-origin-y', `${originY.toFixed(2)}%`);
        style.setProperty('--sticker-render-opacity', opacity.toFixed(4));
        style.setProperty('--sticker-motion-shadow', moving
          ? `drop-shadow(0 ${(2 + (1 - local) * 10).toFixed(2)}px ${(4 + (1 - local) * 12).toFixed(2)}px rgba(0, 0, 0, ${shadowOpacity.toFixed(3)}))`
          : 'drop-shadow(0 0 0 rgb(0 0 0 / 0))');
        sticker.element.classList.toggle('is-sticker-moving', moving);
      });
    }

    goToScene(index) {
      if (!this.running) return;
      const count = this.scenes.length;
      const localIndex = wrap(index, count);
      const current = this.currentPosition ?? 0;
      const destination = Math.round((current - localIndex) / count) * count + localIndex;
      this.cancelSnap();
      this.beginSnap(destination);
    }

    handleKey(event) {
      if (!this.running || event.altKey || event.ctrlKey || event.metaKey) return;
      const directions = { PageDown: 1, ArrowDown: 1, PageUp: -1, ArrowUp: -1 };
      let destination = Math.round(this.currentPosition);
      if (event.key in directions) destination += directions[event.key];
      else if (event.key === 'Home') destination -= wrap(destination, this.scenes.length);
      else if (event.key === 'End') destination += (this.scenes.length - 1) - wrap(destination, this.scenes.length);
      else return;
      event.preventDefault();
      this.cancelMotion();
      this.beginSnap(destination);
    }
  }

  if (!customElements.get('mobile-home-collection')) customElements.define('mobile-home-collection', MobileHomeStage);
})();
